import { connect } from "react-redux";
import { mapStateToProps } from "../../store/helpers/mapState";
import { mapDispatchToProps } from "../../store/helpers/mapDispatch";
import { Box, Button, Card, CardActions } from "@mui/material";
import React, { useEffect, useState } from "react";
import TabPanel from "../../helpers/TabPanel";
import WorklogReportRendering from "./WorklogReportRendering";
import { default as ReactSelect } from "react-select";
import moment from "moment";
import TabBoxComponent from "components/UI/TabBox";

const ProfileWorklogReport = ({ resourceId, systemSettings }) => {
    const [tabValue, setTabValue] = React.useState(0);
    const [list, setList] = useState([]);
    const [projectTaskOptions, setProjectTaskOptions] = useState([]);
    const [searchInfo, setSearchInfo] = useState({
        startDate: "",
        status: "",
        endDate: "",
        projectTask: "",
    });
    const [value, setValue] = useState({});

    const handleChange = (event, newValue) => {
        setTabValue(newValue);
        setSearchInfo(prev => ({ ...prev, projectTask: "" }));
    };

    const updateProjectTaskOptions = () => {
        if (!list || list.length === 0) {
            setProjectTaskOptions([{ label: "All", value: "", id: "all" }]);
            return;
        }

        const currentType = tabValue === 0 ? 'task' : 'project';
        const uniqueItems = new Map();

        list.forEach((item) => {
            const candidates = currentType === 'task'
                ? [item.task, item.subTask]
                : [item.project, item.activity];

            candidates.forEach((itemData) => {
                if (itemData && !uniqueItems.has(itemData.id)) {
                    uniqueItems.set(itemData.id, {
                        label: itemData.name,
                        value: itemData.id,
                        id: itemData.id,
                    });
                }
            });
        });

        const options = [
            { label: "All", value: "", id: "all" },
            ...Array.from(uniqueItems.values()).sort((a, b) => a.label.localeCompare(b.label))
        ];

        setProjectTaskOptions(options);
    };

    useEffect(() => {
        updateProjectTaskOptions();
    }, [list, tabValue]);

    const getSearchParams = (type) => {
        const params = { ...value, type, assignee: resourceId };

        if (params.projectTask) {
            if (type === 'task') {
                params.taskName = params.projectTask;
            } else if (type === 'project') {
                params.projectName = params.projectTask;
            }
            delete params.projectTask;
        }

        return params;
    };

    const mainBackground = systemSettings?.mainBackground;

    const progressStatus = [
        { label: "All", value: "" },
        { label: "Incoming", value: "incoming" },
        { label: "In Progress", value: "in-progress" },
        { label: "Completed", value: "completed" },
        { label: "To verify", value: "to-verify" },
        { label: "On-hold", value: "on-hold" },
        { label: "Overdue", value: "overdue" },
        { label: "Todo", value: "to-do" },
    ];

    return (
        <Card>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setValue(searchInfo);
                }}
            >
                <div className="flex flex-wrap mt-4 mb-4">
                    <div className="w-full xl:w-3/12 mb-12 xl:mb-0 px-2">
                        <label className="mb-1">Start Date</label>
                        <input
                            className="px-2 py-2 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                            value={searchInfo?.startDate}
                            type="datetime-local"
                            onChange={(e) => {
                                setSearchInfo({
                                    ...searchInfo,
                                    startDate: moment(e.target.value).format("YYYY-MM-DD HH:mm:ss"),
                                });
                            }}
                        />
                    </div>
                    <div className="w-full xl:w-3/12 mb-12 xl:mb-0 px-2">
                        <label className="mb-1">End Date</label>
                        <input
                            className="px-2 py-2 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                            value={searchInfo?.endDate}
                            type="datetime-local"
                            onChange={(e) => {
                                setSearchInfo({
                                    ...searchInfo,
                                    endDate: moment(e.target.value).format("YYYY-MM-DD HH:mm:ss"),
                                });
                            }}
                        />
                    </div>
                    <div className="w-full xl:w-3/12 mb-12 xl:mb-0 px-2">
                        <label className="mb-1">Status</label>
                        <ReactSelect
                            options={progressStatus}
                            value={progressStatus.find((v) => v.value === searchInfo?.status) || null}
                            onChange={(v) => setSearchInfo({ ...searchInfo, status: v?.value })}
                        />
                    </div>
                    <div className="w-full xl:w-3/12 mb-12 xl:mb-0 px-2">
                        <label className="mb-1">
                            {tabValue === 0 ? 'Task' : 'Project'}
                        </label>
                        <ReactSelect
                            options={projectTaskOptions}
                            value={projectTaskOptions.find((v) => v.value === searchInfo?.projectTask) || null}
                            onChange={(v) => setSearchInfo({ ...searchInfo, projectTask: v?.value })}
                        />
                    </div>
                </div>
                <CardActions className={"float-end"}>
                    <Button
                        size="small"
                        variant="contained"
                        type={"reset"}
                        color={"error"}
                        onClick={() => {
                            setSearchInfo({
                                startDate: "",
                                endDate: "",
                                status: "",
                                projectTask: "",
                            });
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        sx={{
                            color: "white",
                            backgroundColor: mainBackground ? mainBackground : "#336767",
                            "&:hover": { backgroundColor: mainBackground ? mainBackground : "#336767" },
                            "&:active": { backgroundColor: mainBackground ? mainBackground : "#336767" },
                            "&:focus": { backgroundColor: mainBackground ? mainBackground : "#336767" },
                        }}
                        type={"submit"}
                    >
                        Search
                    </Button>
                </CardActions>
            </form>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabBoxComponent
                    selectedValue={tabValue}
                    handleTabChange={handleChange}
                    tabList={[
                        { label: "Task Report", tabValue: 0, permissionCode: "menu.report" },
                        { label: "Project Report", tabValue: 1, permissionCode: "menu.report" },
                    ]}
                />
                <TabPanel index={0} value={tabValue} p={0}>
                    <WorklogReportRendering
                        list={list}
                        type="task"
                        setList={setList}
                        url={`/api/worklogs/generate/report?info=${JSON.stringify(getSearchParams("task"))}`}
                    />
                </TabPanel>
                <TabPanel index={1} value={tabValue} p={0}>
                    <WorklogReportRendering
                        list={list}
                        type="project"
                        setList={setList}
                        url={`/api/worklogs/generate/report?info=${JSON.stringify(getSearchParams("project"))}`}
                    />
                </TabPanel>
            </Box>
        </Card>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileWorklogReport);
